import { useEffect, useState, useRef } from "react";

/**
 * Debounce utility function
 * @param func Function to debounce
 * @param wait Wait time in milliseconds
 * @returns Debounced function
 */
function debounce<T extends (...args: unknown[]) => void>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;

  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      func(...args);
    };

    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(later, wait);
  };
}

/**
 * Generic hook to track media query matches with debouncing
 * 
 * @param query - Media query string (e.g., "(min-width: 768px)")
 * @param debounceMs - Debounce delay in milliseconds (default: 250ms)
 * @returns Boolean indicating if the media query matches
 * 
 * @example
 * ```tsx
 * const isDesktop = useMediaQuery("(min-width: 1024px)");
 * const isLandscape = useMediaQuery("(orientation: landscape)");
 * ```
 * 
 * @remarks
 * - Uses debouncing to prevent excessive re-renders during window resize
 * - Properly cleans up event listeners to prevent memory leaks
 * - Supports all standard CSS media queries
 */
export function useMediaQuery(
  query: string,
  debounceMs: number = 250
): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    // Initialize with current match state (SSR-safe)
    if (typeof window === "undefined") {
      return false;
    }
    return window.matchMedia(query).matches;
  });

  const mediaQueryRef = useRef<MediaQueryList | null>(null);
  const debouncedHandlerRef = useRef<((event: MediaQueryListEvent) => void) | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    // Create media query list
    const mediaQuery = window.matchMedia(query);
    mediaQueryRef.current = mediaQuery;

    // Create debounced handler
    const handler = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };

    const debouncedHandler = debounce(handler, debounceMs);
    debouncedHandlerRef.current = debouncedHandler;

    // Set initial state
    setMatches(mediaQuery.matches);

    // Add event listener
    if (mediaQuery.addEventListener) {
      // Modern browsers
      mediaQuery.addEventListener("change", debouncedHandler);
    } else {
      // Legacy browsers (fallback)
      mediaQuery.addListener(debouncedHandler);
    }

    // Cleanup function
    return () => {
      if (mediaQueryRef.current && debouncedHandlerRef.current) {
        if (mediaQueryRef.current.removeEventListener) {
          mediaQueryRef.current.removeEventListener("change", debouncedHandlerRef.current);
        } else {
          mediaQueryRef.current.removeListener(debouncedHandlerRef.current);
        }
      }
    };
  }, [query, debounceMs]);

  return matches;
}

